import validateCampaignRules from "@/services/contentful/campaign-rules";
import {
  CampaignFragment,
  RootLayoutCampaignFragment,
} from "@server/gql/graphql";

type ParsedRules = Extract<
  ReturnType<typeof validateCampaignRules>,
  { success: true }
>["data"];

const parsedRulesCache = new WeakMap<CampaignFragment, ParsedRules | null>();

function getParsedRules(campaign: CampaignFragment) {
  const cached = parsedRulesCache.get(campaign);
  if (cached !== undefined) return cached;

  let rules: ParsedRules | null = null;
  if (campaign.rules) {
    const parsed = validateCampaignRules(campaign.rules);
    if (parsed.success && parsed.data.length > 0) {
      rules = parsed.data;
    }
  }

  parsedRulesCache.set(campaign, rules);
  return rules;
}

function localDay(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function campaignHasEnded(campaignEnd: unknown, now = new Date()) {
  if (typeof campaignEnd !== "string") return false;
  const endDay = campaignEnd.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDay)) return false;
  return endDay < localDay(now);
}

function campaignHasStarted(campaignStart: unknown, now = new Date()) {
  if (typeof campaignStart !== "string") return false;
  const startDay = campaignStart.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDay)) return false;
  return startDay <= localDay(now);
}

export const findActiveCampaigns = ({
  flags,
  campaigns,
  brandCodes = [],
  seriesCodes = [],
  designerCodes = [],
  categoryCodes = [],
}: {
  flags: string[];
  campaigns: (CampaignFragment | RootLayoutCampaignFragment | null)[] | null;
  brandCodes?: string[];
  seriesCodes?: string[];
  designerCodes?: string[];
  categoryCodes?: string[];
}) => {
  if (!campaigns?.length) {
    return [];
  }

  const flagSet = new Set(flags);
  const designerSet = new Set(designerCodes);
  const categorySet = new Set(categoryCodes);
  const seriesSet = new Set(seriesCodes);
  const brandSet = new Set(brandCodes);

  return campaigns.filter((campaign) => {
    if (!campaign) return false;
    const active =
      campaignHasStarted(campaign.campaignStart) &&
      !campaignHasEnded(campaign.campaignEnd);
    if (!active) return false;

    const rules = getParsedRules(campaign);
    if (rules) {
      return rules.every((rule) => {
        if (rule.dimension === "flag") {
          return rule.operator === "include"
            ? rule.values.some((value) => flagSet.has(value.code))
            : rule.values.every((value) => !flagSet.has(value.code));
        }
        if (rule.dimension === "brand") {
          return rule.operator === "include"
            ? rule.values.some((value) => brandSet.has(value.code))
            : rule.values.every((value) => !brandSet.has(value.code));
        }
        if (rule.dimension === "designer") {
          return rule.operator === "include"
            ? rule.values.some((value) => designerSet.has(value.code))
            : rule.values.every((value) => !designerSet.has(value.code));
        }
        if (rule.dimension === "category") {
          return rule.operator === "include"
            ? rule.values.some((value) => categorySet.has(value.code))
            : rule.values.every((value) => !categorySet.has(value.code));
        }
        if (rule.dimension === "series") {
          return rule.operator === "include"
            ? rule.values.some((value) => seriesSet.has(value.code))
            : rule.values.every((value) => !seriesSet.has(value.code));
        }
        return false;
      });
    }

    return Boolean(campaign.commerceId && flagSet.has(campaign.commerceId));
  });
};
